import axios, { AxiosResponse } from "axios";
import { environment } from "../../environment";
import { useLogin } from "../login/login";
import {
    CrmMessagesView, CrmThreadVisibility, ICrmEmailInsights, ICrmEmailMessage, ICrmEmailThread, ICrmError, ICrmLeadEmails, ICrmMessagesFilter,
    ICrmReminder, ICrmSendEmailInput, ICrmTeamMember, ICrmThreadAccess, ICrmThreadListItem, ISalesAccess, ISendingProfilePatch
} from "./types";

/** One conversation, with what you may do with it. */
export interface ICrmThreadDetail {
    thread: ICrmEmailThread;
    messages: ICrmEmailMessage[];
    access: ICrmThreadAccess;
}

const filterParams = (f?: ICrmMessagesFilter) => (f ? {
    scope: f.scope, journey: f.journey || undefined, owner: f.owner ?? undefined, sort: f.sort || undefined
} : {});

const crmUrl = `${environment.v2Url}/crm`;

export type CrmEmailKeyFunctions = {
    /** Threads and messages on a lead, for the timeline. */
    listEmails: (leadId: number) => Promise<ICrmLeadEmails | ICrmError>;
    /** Send from the rep's CRM mailbox. Recipient is always the lead; a thread id makes it a reply. */
    sendEmail: (leadId: number, input: ICrmSendEmailInput) => Promise<ICrmEmailMessage | ICrmError>;
    /** The rep's own sending identity: display name, forward-to email, calendar link. */
    updateProfile: (patch: ISendingProfilePatch) => Promise<ISalesAccess | ICrmError>;
    /** Their own CRM photo (cropped square on the server). Answers with the refreshed profile. */
    uploadAvatar: (file: File) => Promise<ISalesAccess | ICrmError>;
    removeAvatar: () => Promise<ISalesAccess | ICrmError>;
    /** Conversations across leads, by pile. */
    listThreads: (view: CrmMessagesView, page?: number, filter?: ICrmMessagesFilter) => Promise<{ results: ICrmThreadListItem[]; count: number } | ICrmError>;
    needsReplyCount: () => Promise<number | ICrmError>;
    threadCounts: (filter?: ICrmMessagesFilter) => Promise<Record<CrmMessagesView, number> | ICrmError>;
    /** Reply times, waiting and response rates over the last 7, 30 or 90 days. */
    getInsights: (days: 7 | 30 | 90) => Promise<ICrmEmailInsights | ICrmError>;
    getThread: (id: number) => Promise<ICrmThreadDetail | ICrmError>;
    /** Teammates, for avatars and pickers. */
    teamMembers: () => Promise<ICrmTeamMember[] | ICrmError>;
    /** Hand a conversation to a teammate (null = nobody). */
    assignThread: (id: number, userId: number | null) => Promise<{ thread: ICrmEmailThread; access: ICrmThreadAccess } | ICrmError>;
    /** Who can see it: private, shared with these people, or the whole team. */
    setThreadAccess: (id: number, patch: { visibility?: CrmThreadVisibility; shared_with?: number[] }) => Promise<{ thread: ICrmEmailThread; access: ICrmThreadAccess } | ICrmError>;
    markHandled: (id: number) => Promise<ICrmEmailThread | ICrmError>;
    snooze: (id: number, until: string, note?: string) => Promise<ICrmReminder | ICrmError>;
    attachThread: (id: number, leadId: number) => Promise<ICrmEmailThread | ICrmError>;
};

/**
 * Rep email inside the CRM. Every send is recorded server-side; nothing here
 * talks to a mail provider directly.
 */
export const useCrmEmail = (): [CrmEmailKeyFunctions] => {
    const [, authToken, loginFunctions] = useLogin();
    const headers = { 'X-Wallmates-Auth': authToken };

    const handle = (err: any): ICrmError => {
        const status = err?.response?.status ?? 0;
        if (status === 401) loginFunctions.logout();
        return {
            ok: false,
            status,
            message: err?.response?.data?.message
                || (status === 0 ? 'Could not reach Wallmates. Check your connection.' : 'Something went wrong.')
        };
    };

    const listEmails = async (leadId: number): Promise<ICrmLeadEmails | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmLeadEmails> = await axios.get(`${crmUrl}/leads/${leadId}/emails`, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const sendEmail = async (leadId: number, input: ICrmSendEmailInput): Promise<ICrmEmailMessage | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmEmailMessage> =
                await axios.post(`${crmUrl}/leads/${leadId}/emails`, input, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const updateProfile = async (patch: ISendingProfilePatch): Promise<ISalesAccess | ICrmError> => {
        try {
            const result: AxiosResponse<ISalesAccess> = await axios.put(`${crmUrl}/me`, patch, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const uploadAvatar = async (file: File): Promise<ISalesAccess | ICrmError> => {
        try {
            const form = new FormData();
            form.append('file', file);
            const result: AxiosResponse<ISalesAccess> = await axios.post(`${crmUrl}/me/avatar`, form, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const removeAvatar = async (): Promise<ISalesAccess | ICrmError> => {
        try {
            const result: AxiosResponse<ISalesAccess> = await axios.delete(`${crmUrl}/me/avatar`, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const listThreads = async (view: CrmMessagesView, page = 0, filter?: ICrmMessagesFilter) => {
        try {
            const result: AxiosResponse<{ results: ICrmThreadListItem[]; count: number }> =
                await axios.get(`${crmUrl}/messages`, { headers, params: { view, page, limit: 40, ...filterParams(filter) } });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const needsReplyCount = async (): Promise<number | ICrmError> => {
        try {
            const result: AxiosResponse<{ needs_reply: number }> = await axios.get(`${crmUrl}/messages/count`, { headers });
            return result.data.needs_reply;
        } catch (err: any) { return handle(err); }
    };

    const getInsights = async (days: 7 | 30 | 90): Promise<ICrmEmailInsights | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmEmailInsights> = await axios.get(`${crmUrl}/messages/insights`, { headers, params: { days }, timeout: 60_000 });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const threadCounts = async (filter?: ICrmMessagesFilter): Promise<Record<CrmMessagesView, number> | ICrmError> => {
        try {
            const result: AxiosResponse<Record<CrmMessagesView, number>> = await axios.get(`${crmUrl}/messages/counts`, { headers, params: filterParams(filter) });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const getThread = async (id: number) => {
        try {
            const result: AxiosResponse<ICrmThreadDetail> = await axios.get(`${crmUrl}/threads/${id}`, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const markHandled = async (id: number): Promise<ICrmEmailThread | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmEmailThread> = await axios.post(`${crmUrl}/threads/${id}/handled`, {}, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const snooze = async (id: number, until: string, note?: string): Promise<ICrmReminder | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmReminder> = await axios.post(`${crmUrl}/threads/${id}/snooze`, { until, note }, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const attachThread = async (id: number, leadId: number): Promise<ICrmEmailThread | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmEmailThread> = await axios.post(`${crmUrl}/threads/${id}/attach`, { lead_id: leadId }, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const teamMembers = async (): Promise<ICrmTeamMember[] | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmTeamMember[]> = await axios.get(`${crmUrl}/team`, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const assignThread = async (id: number, userId: number | null) => {
        try {
            const result: AxiosResponse<{ thread: ICrmEmailThread; access: ICrmThreadAccess }> =
                await axios.post(`${crmUrl}/threads/${id}/assign`, { user_id: userId }, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const setThreadAccess = async (id: number, patch: { visibility?: CrmThreadVisibility; shared_with?: number[] }) => {
        try {
            const result: AxiosResponse<{ thread: ICrmEmailThread; access: ICrmThreadAccess }> =
                await axios.put(`${crmUrl}/threads/${id}/access`, patch, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    return [{ listEmails, sendEmail, updateProfile, uploadAvatar, removeAvatar, listThreads, needsReplyCount, threadCounts, getInsights, getThread, markHandled, snooze, attachThread, teamMembers, assignThread, setThreadAccess }];
};
